"use client";

import * as React from "react";
import { Menu } from "@base-ui-components/react/menu";
import { Collapsible } from "@base-ui-components/react/collapsible";

import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";

function DropdownMenu(props: React.ComponentProps<typeof Menu.Root>) {
  return <Menu.Root {...props} />;
}

function DropdownMenuPortal(props: React.ComponentProps<typeof Menu.Portal>) {
  return <Menu.Portal {...props} />;
}

type DropdownMenuTriggerProps = React.ComponentProps<typeof Menu.Trigger> & {
  /**
   * Compat shim for the radix-style ``asChild`` API: when true, the trigger
   * merges its behavior into the single child element via base-ui's render
   * prop. New code should use ``render={<MyComponent />}`` directly.
   */
  asChild?: boolean;
};

function rendersIntrinsicNonButton(
  renderProp: React.ComponentProps<typeof Menu.Trigger>["render"],
) {
  return (
    React.isValidElement(renderProp) &&
    typeof renderProp.type === "string" &&
    renderProp.type !== "button"
  );
}

function DropdownMenuTrigger({
  asChild,
  children,
  render,
  nativeButton,
  ...props
}: DropdownMenuTriggerProps) {
  const renderProp =
    render ??
    (asChild
      ? (children as React.ReactElement<Record<string, unknown>>)
      : undefined);
  // Base UI assumes trigger replacements are native buttons. Preserve that
  // default for buttons and custom components, but opt direct non-button
  // intrinsic elements like <div> out of native button semantics.
  const resolvedNativeButton =
    nativeButton ?? (rendersIntrinsicNonButton(renderProp) ? false : undefined);
  return (
    <Menu.Trigger
      data-slot="dropdown-menu-trigger"
      render={renderProp}
      nativeButton={resolvedNativeButton}
      {...props}
    >
      {asChild ? undefined : children}
    </Menu.Trigger>
  );
}

type PositionerProps = React.ComponentProps<typeof Menu.Positioner>;

type DropdownMenuContentProps = React.ComponentProps<typeof Menu.Popup> & {
  align?: PositionerProps["align"];
  side?: PositionerProps["side"];
  sideOffset?: PositionerProps["sideOffset"];
  alignOffset?: PositionerProps["alignOffset"];
  collisionPadding?: PositionerProps["collisionPadding"];
  sticky?: PositionerProps["sticky"];
};

function DropdownMenuContent({
  className,
  align,
  side,
  sideOffset = 6,
  alignOffset,
  collisionPadding,
  sticky,
  ...props
}: DropdownMenuContentProps) {
  return (
    <Menu.Portal>
      <Menu.Positioner
        align={align}
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        sticky={sticky}
        className="z-[56]"
      >
        <Menu.Popup
          data-portal-dropdown="true"
          data-slot="dropdown-menu-content"
          className={cn(
            "min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-lg border border-border bg-surface-elevated p-1 shadow-[var(--modal-shadow)] data-[closed]:animate-out data-[closed]:fade-out-0 data-[closed]:zoom-out-95 data-[open]:animate-in data-[open]:fade-in-0 data-[open]:zoom-in-95",
            className,
          )}
          {...props}
        />
      </Menu.Positioner>
    </Menu.Portal>
  );
}

function DropdownMenuGroup(props: React.ComponentProps<typeof Menu.Group>) {
  return <Menu.Group data-slot="dropdown-menu-group" {...props} />;
}

type DropdownMenuItemProps = Omit<
  React.ComponentProps<typeof Menu.Item>,
  "onSelect"
> & {
  inset?: boolean;
  variant?: "default" | "destructive";
  /**
   * Compat alias for the radix-style ``onSelect`` callback. Fires on click
   * (mouse or keyboard activation). Equivalent to ``onClick`` for new code.
   */
  onSelect?: () => void;
};

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  onSelect,
  onClick,
  ...props
}: DropdownMenuItemProps) {
  return (
    <Menu.Item
      data-slot="dropdown-menu-item"
      data-inset={inset || undefined}
      data-variant={variant}
      className={cn(
        "relative flex h-8 cursor-pointer items-center gap-2 rounded px-2.5 text-sm font-normal leading-5 text-heading outline-hidden select-none transition-colors data-[highlighted]:bg-contrast/4 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:data-[highlighted]:bg-destructive/10 data-[variant=destructive]:data-[highlighted]:text-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 data-[variant=destructive]:[&_svg]:text-destructive",
        className,
      )}
      onClick={(e) => {
        onClick?.(e);
        onSelect?.();
      }}
      {...props}
    />
  );
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof Menu.CheckboxItem>) {
  return (
    <Menu.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "relative flex h-8 cursor-pointer items-center gap-2 rounded py-1.5 pr-2.5 pl-8 text-sm font-normal leading-5 text-contrast/75 outline-hidden select-none transition-colors data-[highlighted]:bg-contrast/4 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      checked={checked}
      {...props}
    >
      <span className="pointer-events-none absolute left-2.5 flex size-3.5 items-center justify-center">
        <Menu.CheckboxItemIndicator>
          <Icon name="IconCheckmark1" size={16} />
        </Menu.CheckboxItemIndicator>
      </span>
      {children}
    </Menu.CheckboxItem>
  );
}

function DropdownMenuRadioGroup(
  props: React.ComponentProps<typeof Menu.RadioGroup>,
) {
  return <Menu.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof Menu.RadioItem>) {
  return (
    <Menu.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "relative flex h-8 cursor-pointer items-center gap-2 rounded px-2.5 py-1.5 text-sm font-normal leading-5 text-contrast/75 outline-hidden select-none transition-colors data-[highlighted]:bg-contrast/4 data-[checked]:bg-contrast/8 data-[checked]:text-heading data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      {children}
    </Menu.RadioItem>
  );
}

type DropdownMenuLabelProps = React.ComponentProps<typeof Menu.GroupLabel> & {
  inset?: boolean;
};

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: DropdownMenuLabelProps) {
  // base-ui's Menu.GroupLabel asserts a Menu.Group context, so callers
  // can't drop a bare label into menu content. Self-wrap in a Group to
  // preserve the shadcn ergonomics (`<DropdownMenuLabel>` anywhere).
  return (
    <Menu.Group>
      <Menu.GroupLabel
        data-slot="dropdown-menu-label"
        data-inset={inset || undefined}
        className={cn(
          "px-2.5 py-1.5 text-sm font-normal leading-5 text-contrast/40 data-[inset]:pl-8",
          className,
        )}
        {...props}
      />
    </Menu.Group>
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Menu.Separator>) {
  return (
    <Menu.Separator
      data-slot="dropdown-menu-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "ml-auto text-xs tracking-widest text-contrast/40",
        className,
      )}
      {...props}
    />
  );
}

function DropdownMenuSub(props: React.ComponentProps<typeof Menu.SubmenuRoot>) {
  return <Menu.SubmenuRoot {...props} />;
}

type DropdownMenuSubTriggerProps = React.ComponentProps<
  typeof Menu.SubmenuTrigger
> & {
  inset?: boolean;
};

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: DropdownMenuSubTriggerProps) {
  return (
    <Menu.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset || undefined}
      className={cn(
        "flex h-8 cursor-pointer items-center gap-2 rounded px-2.5 text-sm font-normal leading-5 text-contrast/75 outline-hidden select-none transition-colors data-[highlighted]:bg-contrast/4 data-[inset]:pl-8 data-[open]:bg-contrast/4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      {children}
      <Icon
        name="IconChevronRightSmall"
        size={14}
        className="ml-auto text-contrast/40"
      />
    </Menu.SubmenuTrigger>
  );
}

function DropdownMenuSubContent({
  className,
  align,
  side,
  sideOffset = 6,
  alignOffset,
  collisionPadding,
  sticky,
  ...props
}: DropdownMenuContentProps) {
  return (
    <Menu.Portal>
      <Menu.Positioner
        align={align}
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        sticky={sticky}
        className="z-[56]"
      >
        <Menu.Popup
          data-portal-dropdown="true"
          data-slot="dropdown-menu-sub-content"
          className={cn(
            "min-w-[8rem] overflow-hidden rounded-lg border border-border bg-surface-elevated p-1 shadow-[var(--modal-shadow)] data-[closed]:animate-out data-[closed]:fade-out-0 data-[closed]:zoom-out-95 data-[open]:animate-in data-[open]:fade-in-0 data-[open]:zoom-in-95",
            className,
          )}
          {...props}
        />
      </Menu.Positioner>
    </Menu.Portal>
  );
}

type DropdownMenuCollapsibleProps = {
  label: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Tailwind classes for the trigger row. */
  triggerClassName?: string;
  /** Tailwind classes for the expanding panel container. */
  panelClassName?: string;
};

function DropdownMenuCollapsible({
  label,
  children,
  defaultOpen,
  open,
  onOpenChange,
  triggerClassName,
  panelClassName,
}: DropdownMenuCollapsibleProps) {
  return (
    <Collapsible.Root
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
    >
      <Collapsible.Trigger
        data-slot="dropdown-menu-collapsible-trigger"
        nativeButton={false}
        render={(triggerProps, state) => (
          <Menu.Item
            {...triggerProps}
            closeOnClick={false}
            className={cn(
              "relative flex h-8 w-full cursor-pointer items-center justify-between gap-2 rounded px-2.5 text-sm font-normal leading-5 text-contrast/75 outline-hidden select-none transition-colors data-[highlighted]:bg-contrast/4",
              triggerClassName,
            )}
          >
            <span className="text-foreground">{label}</span>
            <Icon
              name="IconChevronDownMedium"
              className={cn(
                "text-muted-foreground size-4 transition-transform duration-200",
                state.open && "rotate-180",
              )}
            />
          </Menu.Item>
        )}
      />
      <Collapsible.Panel
        data-slot="dropdown-menu-collapsible-panel"
        className={cn(
          "overflow-hidden transition-[height] duration-200 ease-out h-[var(--collapsible-panel-height)] data-[ending-style]:h-0 data-[starting-style]:h-0",
          panelClassName,
        )}
      >
        {children}
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuCollapsible,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
